// =====================================================================
//  FORMATO — las mismas funciones del prototipo
//
//  fmt() y fmtn() vienen del prototipo original tal cual, para que los
//  montos se vean idénticos: $41.845.320, con punto de millar chileno.
// =====================================================================

import type { Cifra } from '../tipos/dominio'

/**
 * Normaliza una cifra que puede venir como string.
 *
 * PostgREST entrega las columnas `numeric` como string para no perder
 * precisión al pasar por JSON. Todos los montos del esquema son
 * numeric(14,0) o numeric(10,4), así que llegan como '5720480'.
 */
export const num = (v: Cifra | undefined): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/** $5.720.480 */
export const fmt = (v: Cifra | undefined): string =>
  '$' + Math.round(num(v)).toLocaleString('es-CL')

/** 5.720.480 */
export const fmtn = (v: Cifra | undefined): string =>
  Math.round(num(v)).toLocaleString('es-CL')

/** 80 · 9,6 · 0,4 — las UTM llevan coma decimal y sin ceros de relleno */
export const fmtUtm = (v: Cifra | undefined): string =>
  num(v).toLocaleString('es-CL', { maximumFractionDigits: 4 })

/** Lo que puede llegar en un campo de fecha, según de dónde venga. */
export type Fechable = string | Date | null | undefined

/**
 * Convierte a Date cualquiera de las tres formas en que llega una fecha, y
 * resuelve dos problemas concretos.
 *
 * **El día corrido.** Las columnas `date` llegan como 'YYYY-MM-DD', y
 * `new Date('2026-09-20')` se interpreta como medianoche UTC. En
 * America/Santiago eso se muestra como el 19 de septiembre. No es
 * cosmético: el 20 de septiembre es el límite legal de publicación en el
 * Diario Oficial (art. 147), y `fecha_pago_patente` es la que acredita que
 * el titular pagó el doble ANTES del remate (art. 149 inc. 2). Un día de
 * corrimiento cambia el sentido del dato. Por eso las fechas de calendario
 * se anclan al mediodía LOCAL, que es inmune al huso y al horario de
 * verano.
 *
 * **Las dos serializaciones.** PostgREST entrega las columnas `date` como
 * 'YYYY-MM-DD'; el protocolo nativo de Postgres —que usa el modo
 * demostración y las pruebas— las entrega como Date o como ISO completo.
 * Concatenar 'T12:00:00' sobre un ISO completo produce Invalid Date.
 *
 * Los `timestamptz` (fecha_remate, actualizado, creado) son instantes de
 * verdad y caen por el camino de abajo, que es el correcto para ellos.
 */
export const comoFecha = (v: Fechable): Date | null => {
  if (v === null || v === undefined || v === '') return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v

  const calendario = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v)
  if (calendario) {
    const anio = Number(calendario[1])
    const mes = Number(calendario[2]) - 1
    const dia = Number(calendario[3])
    const d = new Date(anio, mes, dia, 12)
    // El constructor de Date NO valida: new Date(2026, 12, 45) rueda hasta
    // febrero de 2027 sin avisar. Se comprueba que los componentes vuelvan
    // iguales, porque una fecha que no existe tiene que dar null y no una
    // fecha distinta — acá se guardan plazos legales.
    if (d.getFullYear() !== anio || d.getMonth() !== mes || d.getDate() !== dia) return null
    return d
  }

  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

/** 21 de octubre de 2026 */
export const fecha = (v: Fechable): string => {
  const d = comoFecha(v)
  return d ? d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'
}

/** 21-10-2026 12:00 */
export const fechaHora = (v: Fechable): string => {
  const d = comoFecha(v)
  return d ? d.toLocaleString('es-CL') : '—'
}

/** 2026-10-21 — para los `<input type="date">`, que solo aceptan esta forma. */
export const fechaISO = (v: Fechable): string => {
  const d = comoFecha(v)
  if (!d) return ''
  const mes = String(d.getMonth() + 1).padStart(2, '0')
  const dia = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${mes}-${dia}`
}

/** Días corridos desde hoy hasta la fecha dada. Negativo si ya pasó. */
export const diasHasta = (v: Fechable): number => {
  const d = comoFecha(v)
  if (!d) return 0
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000)
}

/** 3 concesiones · 1 concesión */
export const plural = (n: number, singular: string, plural_: string): string =>
  `${n} ${n === 1 ? singular : plural_}`

/** Color de la barra de puntaje, igual que en el prototipo. */
export const colorPuntaje = (p: number): string =>
  p > 66 ? 'var(--ok)' : p > 40 ? 'var(--warn)' : 'var(--bad)'
